import React, { lazy, Suspense } from 'react';
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { PlatformThemeProvider } from './contexts/PlatformThemeContext';
import { AudioPlayerProvider } from './contexts/AudioPlayerContext';
import { ExecutivePrivilegesProvider } from './contexts/ExecutivePrivilegesContext';
import DeferredMount from './components/DeferredMount';
import ImpersonationBanner from './components/ImpersonationBanner';
import RolePreviewBanner from './components/RolePreviewBanner';
// Non-critical UI — lazy so `sonner` + its motion deps, the chat-widget scraper,
// and the floating help pill are NOT in the initial main.js chunk. Each of these
// only matters after first paint; landing visitors on mobile Slow-4G recover
// hundreds of milliseconds of JS parse time.
const Toaster = lazy(() =>
  import('./components/ui/sonner').then((m) => ({ default: m.Toaster }))
);
const GHLChatWidget = lazy(() => import('./components/GHLChatWidget'));
const FloatingHelper = lazy(() => import('./components/FloatingHelper'));
const InlineTextEditor = lazy(() => import('./components/admin/InlineTextEditor'));
import VoiceTourOverridesLoader from './components/VoiceTourOverridesLoader';
// AmbientGuide is currently commented out in the tree — no import needed.

// Lazy-loaded page fallback
const PageLoader = () => (
  <div style={{display:'flex',justifyContent:'center',alignItems:'center',height:'60vh'}}>
    <div style={{width:32,height:32,border:'3px solid #27272a',borderTop:'3px solid #E31B23',borderRadius:'50%',animation:'spin 0.8s linear infinite'}} />
    <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
  </div>
);

// Core pages (keep eager - needed for first paint)
import LandingPage from './pages/LandingPage';
// AuthPage + role Layouts are lazy-loaded so a landing-page visitor never
// downloads Admin/Executive/Scout/Curator/RadioStation code they'll never see.
// Suspense already wraps the whole <Routes> tree below.
const AuthPage = lazy(() => import('./pages/AuthPage'));

// Lazy-loaded pages
const AuthCallback = lazy(() => import('./pages/AuthCallback'));
const SocialAuthCallback = lazy(() => import('./pages/SocialAuthCallback'));
const SoundCloudComplete = lazy(() => import('./pages/SoundCloudComplete'));
const ForgotPassword = lazy(() => import('./pages/ForgotPassword'));
const ResetPassword = lazy(() => import('./pages/ResetPassword'));
const AcceptScoutInvite = lazy(() => import('./pages/AcceptScoutInvite'));
const ScoutJoinByCode = lazy(() => import('./pages/ScoutJoinByCode'));
const LoyaltyJoinByCode = lazy(() => import('./pages/LoyaltyJoinByCode'));
const AcceptInvite = lazy(() => import('./pages/AcceptInvite'));
const ClaimAccount = lazy(() => import('./pages/ClaimAccount'));
const ClaimRadioStation = lazy(() => import('./pages/ClaimRadioStation'));
const ClaimCurator = lazy(() => import('./pages/ClaimCurator'));
const CompleteApplication = lazy(() => import('./pages/CompleteApplication'));
const ExecutiveClaim = lazy(() => import('./pages/ExecutiveClaim'));
const ArtistClaim = lazy(() => import('./pages/ArtistClaim'));
const ArtistLoyaltyMembership = lazy(() => import('./pages/ArtistLoyaltyMembership'));
const ClaimFinalize = lazy(() => import('./pages/ClaimFinalize'));
const LoyaltyRenewal = lazy(() => import('./pages/LoyaltyRenewal'));
const PaymentSuccess = lazy(() => import('./pages/PaymentSuccess'));
const MeetingDepositPage = lazy(() => import('./pages/MeetingDepositPage'));
const SmartRedirect = lazy(() => import('./pages/SmartRedirect'));
const LegalPage = lazy(() => import('./pages/LegalPage'));
const GuestReviewPage = lazy(() => import('./pages/GuestReviewPage'));
const TeamManagementPage = lazy(() => import('./pages/TeamManagementPage'));
const StationLibraryPage = lazy(() => import('./pages/StationLibraryPage'));
const TeamInviteAcceptPage = lazy(() => import('./pages/TeamInviteAcceptPage'));
const HelpCenter = lazy(() => import('./pages/HelpCenter'));

// Layouts (keep eager - structural)
const ArtistLayout = lazy(() => import('./layouts/ArtistLayout'));
const ExecutiveLayout = lazy(() => import('./layouts/ExecutiveLayout'));
const ScoutLayout = lazy(() => import('./layouts/ScoutLayout'));
const AdminLayout = lazy(() => import('./layouts/AdminLayout'));
const RadioStationLayout = lazy(() => import('./layouts/RadioStationLayout'));
const PlaylistCuratorLayout = lazy(() => import('./layouts/PlaylistCuratorLayout'));

// Artist Pages - lazy
const ArtistDashboard = lazy(() => import('./pages/artist/ArtistDashboard'));
const ArtistDashboardLegacy = lazy(() => import('./pages/artist/ArtistDashboardLegacy'));
const HitAnalystPage = lazy(() => import('./pages/artist/HitAnalystPage'));
const DemoDoctorPage = lazy(() => import('./pages/artist/DemoDoctorPage'));
const ArtistSongs = lazy(() => import('./pages/artist/ArtistSongs'));
const DiscoverExecutives = lazy(() => import('./pages/artist/DiscoverExecutives'));
const DiscoverMusic = lazy(() => import('./pages/artist/DiscoverMusic'));
const ArtistSubmissions = lazy(() => import('./pages/artist/ArtistSubmissions'));
const ArtistProfile = lazy(() => import('./pages/artist/ArtistProfile'));
const ArtistMessages = lazy(() => import('./pages/artist/ArtistMessages'));
const ArtistWallet = lazy(() => import('./pages/artist/ArtistWallet'));
const ArtistWalletLegacy = lazy(() => import('./pages/artist/ArtistWalletLegacy'));

// Executive Pages - lazy
const ExecutiveDashboard = lazy(() => import('./pages/executive/ExecutiveDashboard'));
const ExecutiveQueue = lazy(() => import('./pages/executive/ExecutiveQueue'));
const HitList = lazy(() => import('./pages/executive/HitList'));
const HitListCharts = lazy(() => import('./pages/executive/HitListCharts'));
const SavedSongs = lazy(() => import('./pages/executive/SavedSongs'));
const ScoutManagement = lazy(() => import('./pages/executive/ScoutManagement'));
const ScoutPerformance = lazy(() => import('./pages/executive/ScoutPerformance'));
const ExecutiveProfile = lazy(() => import('./pages/executive/ExecutiveProfile'));
const ExecutiveMessages = lazy(() => import('./pages/executive/ExecutiveMessages'));
const ExecutiveOnboarding = lazy(() => import('./pages/executive/ExecutiveOnboarding'));
const SetupServicesPage = lazy(() => import('./pages/executive/SetupServicesPage'));
const ExecutivePayouts = lazy(() => import('./pages/executive/ExecutivePayouts'));
const ExecutiveSessionHistory = lazy(() => import('./pages/executive/ExecutiveSessionHistory'));
const ExecutiveLiveSession = lazy(() => import('./pages/executive/ExecutiveLiveSession'));
const EvaluationForm = lazy(() => import('./pages/executive/EvaluationForm'));
const ExecutiveRadio = lazy(() => import('./pages/executive/ExecutiveRadio'));

// Scout Pages - lazy
const ScoutDashboard = lazy(() => import('./pages/scout/ScoutDashboard'));
const ScoutQueue = lazy(() => import('./pages/scout/ScoutQueue'));
const ScoutHitList = lazy(() => import('./pages/scout/ScoutHitList'));
const ScoutMessages = lazy(() => import('./pages/scout/ScoutMessages'));
const ScoutSharedSongs = lazy(() => import('./pages/scout/ScoutSharedSongs'));
const ScoutProfile = lazy(() => import('./pages/scout/ScoutProfile'));

// Artist Imports - lazy
const ArtistImports = lazy(() => import('./pages/artist/ArtistImports'));

// Executive Imports - lazy
const ExecutiveImports = lazy(() => import('./pages/executive/ExecutiveImports'));

// Admin Pages - lazy
const AdminDashboard = lazy(() => import('./pages/admin/AdminDashboard'));
const AdminAccounting = lazy(() => import('./pages/admin/AdminAccounting'));
const AdminAccountingDrift = lazy(() => import('./pages/admin/AdminAccountingDrift'));
const AdminCreditPacks = lazy(() => import('./pages/admin/AdminCreditPacks'));
const AdminPayouts = lazy(() => import('./pages/admin/AdminPayouts'));
const AdminModeration = lazy(() => import('./pages/admin/AdminModeration'));
const AdminScoutQueue = lazy(() => import('./pages/admin/AdminScoutQueue'));
const AdminAuditLog = lazy(() => import('./pages/admin/AdminAuditLog'));
const AdminImports = lazy(() => import('./pages/admin/AdminImports'));
const AdminPlans = lazy(() => import('./pages/admin/AdminPlans'));
const AdminCMS = lazy(() => import('./pages/admin/AdminCMS'));
const AdminAnalytics = lazy(() => import('./pages/admin/AdminAnalytics'));
const AdminSettings = lazy(() => import('./pages/admin/AdminSettings'));
const AdminMessages = lazy(() => import('./pages/admin/AdminMessages'));
const AdminSubAdmins = lazy(() => import('./pages/admin/AdminSubAdmins'));
const AdminEmailPortfolio = lazy(() => import('./pages/admin/AdminEmailPortfolio'));
const AdminEvaluations = lazy(() => import('./pages/admin/AdminEvaluations'));
const AdminExports = lazy(() => import('./pages/admin/AdminExports'));
const AdminUsers = lazy(() => import('./pages/admin/AdminUsers'));
const AdminRadioStations = lazy(() => import('./pages/admin/AdminRadioStations'));
const AdminPlaylistCurators = lazy(() => import('./pages/admin/AdminPlaylistCurators'));
const AdminStripePayments = lazy(() => import('./pages/admin/AdminStripePayments'));
const AdminPaymentGateway = lazy(() => import('./pages/admin/AdminPaymentGateway'));
const AdminGHLDashboard = lazy(() => import('./pages/admin/AdminGHLDashboard'));
const AdminActivity = lazy(() => import('./pages/admin/AdminActivity'));
const AdminBackups = lazy(() => import('./pages/admin/AdminBackups'));
const AdminBillboard = lazy(() => import('./pages/admin/AdminBillboard'));
const AdminAdAnalytics = lazy(() => import('./pages/admin/AdminAdAnalytics'));
const AdminSubmissions = lazy(() => import('./pages/admin/AdminSubmissions'));
const AdminExecutiveImport = lazy(() => import('./pages/admin/AdminExecutiveImport'));
const AdminReconciliation = lazy(() => import('./pages/admin/AdminReconciliation'));
const AdminCuratorPricing = lazy(() => import('./pages/admin/AdminCuratorPricing'));
const AdminSuggestions = lazy(() => import('./pages/admin/AdminSuggestions'));
const AdminPendingApprovals = lazy(() => import('./pages/admin/AdminPendingApprovals'));
const AdminTestCleanup = lazy(() => import('./pages/admin/AdminTestCleanup'));
const AdminAccountingReset = lazy(() => import('./pages/admin/AdminAccountingReset'));
const AdminSongScreening = lazy(() => import('./pages/admin/AdminSongScreening'));
const AdminProfileNudge = lazy(() => import('./pages/admin/AdminProfileNudge'));
const AdminSupportPage = lazy(() => import('./pages/admin/AdminSupportPage'));
const AdminArtistLookup = lazy(() => import('./pages/admin/AdminArtistLookup'));
const AdminKnowledgeBase = lazy(() => import('./pages/admin/AdminKnowledgeBase'));
const AdminTours = lazy(() => import('./pages/admin/AdminTours'));
const AdminVoiceTours = lazy(() => import('./pages/admin/AdminVoiceTours'));
const AdminThemes = lazy(() => import('./pages/admin/AdminThemes'));
const AdminLandingStats = lazy(() => import('./pages/admin/AdminLandingStats'));
const AdminLoyaltyUsers = lazy(() => import('./pages/admin/AdminLoyaltyUsers'));
const AdminTwilioHealth = lazy(() => import('./pages/admin/AdminTwilioHealth'));
const AdminStripeHealth = lazy(() => import('./pages/admin/AdminStripeHealth'));
const AdminOutreachCampaigns = lazy(() => import('./pages/admin/AdminOutreachCampaigns'));
const AdminArchive = lazy(() => import('./pages/admin/AdminArchive'));
const AdminAISupport = lazy(() => import('./pages/admin/AdminAISupport'));
const AdminUnclaimedAccepting = lazy(() => import('./pages/admin/AdminUnclaimedAccepting'));
const AdminAlerts = lazy(() => import('./pages/admin/AdminAlerts'));
const AdminExecutiveSessions = lazy(() => import('./pages/admin/AdminExecutiveSessions'));
const AdminBookingAgent = lazy(() => import('./pages/admin/AdminBookingAgent'));

// Membership Pages - lazy
const ArtistMembership = lazy(() => import('./pages/artist/ArtistMembership'));
const UpgradeMembership = lazy(() => import('./pages/artist/UpgradeMembership'));
const LoyaltyBenefitsPage = lazy(() => import('./pages/artist/LoyaltyBenefitsPage'));
const NotificationSettingsPage = lazy(() => import('./pages/artist/NotificationSettingsPage'));
const ReferralsPage = lazy(() => import('./pages/artist/ReferralsPage'));
const ExecutiveMembership = lazy(() => import('./pages/executive/ExecutiveMembership'));

// Radio Station Pages - lazy
const RadioStationDashboard = lazy(() => import('./pages/radio-station/RadioStationDashboard'));
const RadioStationQueue = lazy(() => import('./pages/radio-station/RadioStationQueue'));
const RadioStationQueueDetail = lazy(() => import('./pages/radio-station/RadioStationQueueDetail'));
const RadioStationProfile = lazy(() => import('./pages/radio-station/RadioStationProfile'));
const RadioStationAnalytics = lazy(() => import('./pages/radio-station/RadioStationAnalytics'));
const RadioStationMessages = lazy(() => import('./pages/radio-station/RadioStationMessages'));
const RadioStationInviteTalent = lazy(() => import('./pages/radio-station/RadioStationInviteTalent'));
const RadioStationHitList = lazy(() => import('./pages/radio-station/RadioStationHitList'));
const RadioStationSavedSongs = lazy(() => import('./pages/radio-station/RadioStationSavedSongs'));

// Playlist Curator Pages - lazy
const PlaylistCuratorDashboard = lazy(() => import('./pages/playlist-curator/PlaylistCuratorDashboard'));
const PlaylistCuratorQueue = lazy(() => import('./pages/playlist-curator/PlaylistCuratorQueue'));
const PlaylistCuratorQueueDetail = lazy(() => import('./pages/playlist-curator/PlaylistCuratorQueueDetail'));
const PlaylistCuratorProfile = lazy(() => import('./pages/playlist-curator/PlaylistCuratorProfile'));
const PlaylistCuratorMembership = lazy(() => import('./pages/playlist-curator/PlaylistCuratorMembership'));
const PlaylistCuratorAnalytics = lazy(() => import('./pages/playlist-curator/PlaylistCuratorAnalytics'));
const PlaylistCuratorMessages = lazy(() => import('./pages/playlist-curator/PlaylistCuratorMessages'));
const PlaylistCuratorSavedSongs = lazy(() => import('./pages/playlist-curator/PlaylistCuratorSavedSongs'));
const PlaylistCuratorOnboarding = lazy(() => import('./pages/playlist-curator/PlaylistCuratorOnboarding'));

// Subscription pages - lazy
const SelectPlan = lazy(() => import('./pages/subscription/SelectPlan'));
const SubscriptionSuccess = lazy(() => import('./pages/subscription/SubscriptionSuccess'));
const SubscriptionCancel = lazy(() => import('./pages/subscription/SubscriptionCancel'));

// Ticket pages - lazy
const TicketWorkspace = lazy(() => import('./pages/tickets/TicketWorkspace'));
const TicketList = lazy(() => import('./pages/tickets/TicketList'));

// Curator Ratings - lazy
const PendingCuratorRatings = lazy(() => import('./pages/artist/PendingCuratorRatings'));
const ProEvaluationThankYou = lazy(() => import('./pages/artist/ProEvaluationThankYou'));
const ArtistSessionRecap = lazy(() => import('./pages/artist/ArtistSessionRecap'));
const CancelSubscriptionConfirm = lazy(() => import('./pages/artist/CancelSubscriptionConfirm'));

// Support Page - lazy
const SupportPage = lazy(() => import('./pages/SupportPage'));

// Protected Route wrapper
const ProtectedRoute = ({ children, allowedRoles }) => {
  const { isAuthenticated, user, loading, isRolePreview } = useAuth();
  const location = useLocation();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-500" />
      </div>
    );
  }
  
  if (!isAuthenticated) {
    const dest = `${location.pathname}${location.search}`;
    return <Navigate to={dest && dest !== '/' ? `/auth?next=${encodeURIComponent(dest)}` : '/auth'} replace />;
  }
  
  // Artists must select a plan before accessing the platform.
  // Exception: when returning from a Stripe payment, let them through so they
  // land on the page that initiated the purchase. We accept multiple post-pay
  // signals because different flows (regular subscription vs loyalty claim)
  // append different query params: `payment=success`, `welcome=1`, or the
  // raw Stripe `session_id`. RequireAuth won't gate on a stale `user.has_
  // selected_plan` until the page has had a chance to call refreshUser().
  const isPaymentReturn = typeof window !== 'undefined' && (
    window.location.search?.includes('payment=success') ||
    window.location.search?.includes('welcome=1') ||
    window.location.search?.includes('session_id=')
  );
  if (user?.role === 'artist' && user?.has_selected_plan === false && !isPaymentReturn && !isRolePreview) {
    return <Navigate to="/subscription/plans?role=artist" replace />;
  }
  
  if (allowedRoles && !allowedRoles.includes(user?.role)) {
    // Redirect to appropriate dashboard
    switch (user?.role) {
      case 'artist':
        return <Navigate to="/artist" replace />;
      case 'executive':
        return <Navigate to="/executive" replace />;
      case 'scout':
        return <Navigate to="/scout" replace />;
      case 'admin':
        return <Navigate to="/admin" replace />;
      case 'radio_station':
        return <Navigate to="/radio-station" replace />;
      case 'playlist_curator':
        return <Navigate to="/playlist-curator" replace />;
      default:
        return <Navigate to="/" replace />;
    }
  }
  
  return children;
};

function AppRoutes() {
  const location = useLocation();
  
  // CRITICAL: Check URL fragment for session_id synchronously during render
  // This prevents race conditions by processing OAuth callback FIRST
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }
  
  return (
    <Suspense fallback={<PageLoader />}>
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<LandingPage />} />
      {/* /auth is intentionally NOT wrapped in AuthRedirect — even an
          already-signed-in user should be able to reach this page to sign
          in as a different account. Submitting the form with new creds
          replaces the existing session via AuthContext.login(). */}
      <Route path="/auth" element={<AuthPage />} />
      <Route path="/auth/social-callback" element={<SocialAuthCallback />} />
      <Route path="/auth/soundcloud/complete" element={<SoundCloudComplete />} />
      <Route path="/login" element={<Navigate to="/auth?tab=login" replace />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/help" element={<HelpCenter />} />
      <Route path="/claim/:token" element={<ClaimAccount />} />
      <Route path="/claim-radio/:token" element={<ClaimRadioStation />} />
      <Route path="/claim-curator/:token" element={<ClaimCurator />} />
      <Route path="/complete-application/:token" element={<CompleteApplication />} />
      {/* A&R self-claim landing — public, invited execs land here from the
          "Claim my account" button in their invitation email. */}
      <Route path="/executive/claim" element={<ExecutiveClaim />} />
      <Route path="/ar/claim" element={<ExecutiveClaim />} />
      {/* Loyalty-artist self-claim landing — public, invited grandfathered
          artists land here from the "Claim your account" button in their
          loyalty invitation email. */}
      <Route path="/artist/claim" element={<ArtistClaim />} />
      {/* Membership card page shown right after a loyalty artist finishes
          the claim flow. Detects existing Stripe subs (no double-charge)
          or offers the grandfathered $9.99/mo activation. Requires auth
          since the just-claimed JWT gates it. */}
      <Route path="/artist/claim/membership" element={<ArtistLoyaltyMembership />} />
      {/* Yearly founding member renewal landing — linked from the T-7 day
          renewal-warning email. Choose: stay at $9.99/mo OR upgrade to
          Loyalty Pro $199/yr. Public, no auth (token-based). */}
      <Route path="/loyalty/renewal/:token" element={<LoyaltyRenewal />} />
      {/* Post-Stripe landing for loyalty-claim checkouts — synchronously
          provisions the plan before sending the user to /artist, so they
          never hit the RequireAuth redirect loop on a stale has_selected_plan. */}
      <Route path="/claim-finalize" element={<ClaimFinalize />} />
      {/* Universal post-payment "Thank You" landing — PUBLIC so a stale JWT
          coming back from Stripe never kicks the user to /auth. All Stripe
          success URLs route here with query params describing what was bought. */}
      <Route path="/payment/success" element={<PaymentSuccess />} />
      <Route path="/meeting-deposit/:token" element={<MeetingDepositPage />} />
      <Route path="/go" element={<SmartRedirect />} />
      {/* Legal pages — indexable. /legal is canonical; the aliases set a
          different <title>/meta description and scroll to their section. */}
      <Route path="/legal" element={<LegalPage />} />
      <Route path="/privacy" element={<LegalPage />} />
      <Route path="/terms" element={<LegalPage />} />
      <Route path="/fulfillment" element={<LegalPage />} />
      <Route path="/scout/accept" element={<AcceptScoutInvite />} />
      <Route path="/scout/join" element={<ScoutJoinByCode />} />
      <Route path="/founding/join" element={<LoyaltyJoinByCode />} />
      <Route path="/loyalty/join" element={<LoyaltyJoinByCode />} />
      <Route path="/accept-invite" element={<AcceptInvite />} />
      
      {/* Subscription/Payment Routes */}
      <Route path="/subscription/plans" element={<SelectPlan />} />
      <Route path="/subscription/success" element={<SubscriptionSuccess />} />
      <Route path="/subscription/cancel" element={<SubscriptionCancel />} />
      
      {/* Guest Review Portal (no auth required) */}
      <Route path="/review/:submissionId/:token" element={<GuestReviewPage />} />
      
      {/* Team Invite Accept (redirects to login if needed) */}
      <Route path="/team/accept/:token" element={<TeamInviteAcceptPage />} />
      
      {/* Station/Curator Team & Library (auth required) */}
      <Route path="/station/team/:stationType/:stationId" element={
        <ProtectedRoute allowedRoles={['radio_station', 'playlist_curator', 'admin']}>
          <TeamManagementPage />
        </ProtectedRoute>
      } />
      <Route path="/station/library/:stationType/:stationId" element={
        <ProtectedRoute allowedRoles={['radio_station', 'playlist_curator', 'admin']}>
          <StationLibraryPage />
        </ProtectedRoute>
      } />

      {/* Artist Routes */}
      <Route path="/artist" element={
        <ProtectedRoute allowedRoles={['artist']}>
          <ArtistLayout />
        </ProtectedRoute>
      }>
        <Route index element={<ArtistDashboard />} />
        <Route path="dashboard-legacy" element={<ArtistDashboardLegacy />} />
        <Route path="songs" element={<ArtistSongs />} />
        <Route path="hit-analyst" element={<HitAnalystPage />} />
        <Route path="demo-doctor" element={<DemoDoctorPage />} />
        <Route path="discover" element={<DiscoverExecutives />} />
        <Route path="discover-music" element={<DiscoverMusic />} />
        <Route path="submissions" element={<ArtistSubmissions />} />
        <Route path="wallet" element={<ArtistWallet />} />
        <Route path="wallet-legacy" element={<ArtistWalletLegacy />} />
        <Route path="membership" element={<ArtistMembership />} />
        <Route path="upgrade-membership" element={<UpgradeMembership />} />
        <Route path="loyalty-benefits" element={<LoyaltyBenefitsPage />} />
        <Route path="profile" element={<ArtistProfile />} />
        <Route path="messages" element={<ArtistMessages />} />
        <Route path="imports" element={<ArtistImports />} />
        <Route path="tickets" element={<TicketList userType="artist" />} />
        <Route path="tickets/:ticketId" element={<TicketWorkspace />} />
        <Route path="rate-curators" element={<PendingCuratorRatings />} />
        <Route path="evaluation/thank-you" element={<ProEvaluationThankYou />} />
        <Route path="sessions/:requestId" element={<ArtistSessionRecap />} />
        <Route path="subscription/cancel" element={<CancelSubscriptionConfirm />} />
        <Route path="support" element={<SupportPage role="artist" />} />
        <Route path="notification-settings" element={<NotificationSettingsPage />} />
                <Route path="referrals" element={<ReferralsPage />} />
      </Route>

      {/* Radio Station Routes */}
      <Route path="/radio-station" element={
        <ProtectedRoute allowedRoles={['radio_station']}>
          <RadioStationLayout />
        </ProtectedRoute>
      }>
        <Route index element={<RadioStationDashboard />} />
        <Route path="queue" element={<RadioStationQueue />} />
        <Route path="queue/:submissionId" element={<RadioStationQueueDetail />} />
        <Route path="saved-songs" element={<RadioStationSavedSongs />} />
        <Route path="stats" element={<RadioStationAnalytics />} />
        <Route path="profile" element={<RadioStationProfile />} />
        <Route path="messages" element={<RadioStationMessages />} />
        <Route path="invite-talent" element={<RadioStationInviteTalent />} />
        <Route path="hit-list" element={<RadioStationHitList />} />
        <Route path="support" element={<SupportPage role="radio_station" />} />
      </Route>

      {/* Playlist Curator Routes */}
      {/* Onboarding lives OUTSIDE the layout so the first-run flow gets a
          full-bleed setup wizard, matching /executive/onboarding. */}
      <Route path="/playlist-curator/onboarding" element={
        <ProtectedRoute allowedRoles={['playlist_curator']}>
          <PlaylistCuratorOnboarding />
        </ProtectedRoute>
      } />

      <Route path="/playlist-curator" element={
        <ProtectedRoute allowedRoles={['playlist_curator']}>
          <PlaylistCuratorLayout />
        </ProtectedRoute>
      }>
        <Route index element={<PlaylistCuratorDashboard />} />
        <Route path="queue" element={<PlaylistCuratorQueue />} />
        <Route path="queue/:submissionId" element={<PlaylistCuratorQueueDetail />} />
        <Route path="saved-songs" element={<PlaylistCuratorSavedSongs />} />
        <Route path="stats" element={<PlaylistCuratorAnalytics />} />
        <Route path="analytics" element={<PlaylistCuratorAnalytics />} />
        <Route path="messages" element={<PlaylistCuratorMessages />} />
        <Route path="profile" element={<PlaylistCuratorProfile />} />
        <Route path="membership" element={<PlaylistCuratorMembership />} />
        <Route path="support" element={<SupportPage role="playlist_curator" />} />
      </Route>

      {/* Executive Routes */}
      {/* Executive Onboarding (outside layout) */}
      <Route path="/executive/onboarding" element={
        <ProtectedRoute allowedRoles={['executive']}>
          <ExecutiveOnboarding />
        </ProtectedRoute>
      } />

      <Route path="/executive/setup-services" element={
        <ProtectedRoute allowedRoles={['executive']}>
          <SetupServicesPage />
        </ProtectedRoute>
      } />

      <Route path="/executive" element={
        <ProtectedRoute allowedRoles={['executive']}>
          <ExecutiveLayout />
        </ProtectedRoute>
      }>
        <Route index element={<ExecutiveDashboard />} />
        <Route path="queue" element={<ExecutiveQueue />} />
        <Route path="queue/:submissionId" element={<ExecutiveQueue />} />
        <Route path="evaluation/:submissionId" element={<EvaluationForm />} />
        <Route path="hit-list" element={<HitList />} />
        <Route path="charts" element={<HitListCharts />} />
        <Route path="saved-songs" element={<SavedSongs />} />
        {/* <Route path="radio" element={<ExecutiveRadio />} /> ON HOLD */}
        <Route path="scouts" element={<ScoutManagement />} />
        <Route path="scout-performance" element={<ScoutPerformance />} />
        <Route path="membership" element={<ExecutiveMembership />} />
        <Route path="services" element={<SetupServicesPage />} />
        <Route path="payouts" element={<ExecutivePayouts />} />
        <Route path="sessions" element={<ExecutiveSessionHistory />} />
        <Route path="session/:requestId/live" element={<ExecutiveLiveSession />} />
        <Route path="profile" element={<ExecutiveProfile />} />
        <Route path="messages" element={<ExecutiveMessages />} />
        <Route path="imports" element={<ExecutiveImports />} />
        <Route path="tickets" element={<TicketList userType="executive" />} />
        <Route path="tickets/:ticketId" element={<TicketWorkspace />} />
        <Route path="support" element={<SupportPage role="executive" />} />
        {/* Artist Tools — executives can submit songs OUT to playlist curators
            and radio stations using the same flows artists use. These routes
            reuse the artist components but live under /executive/ so the
            executive sidebar stays visible and the context never feels lost. */}
        <Route path="songs" element={<ArtistSongs />} />
        <Route path="discover" element={<DiscoverExecutives />} />
        <Route path="discover-music" element={<DiscoverMusic />} />
        <Route path="submissions" element={<ArtistSubmissions />} />
        <Route path="wallet" element={<ArtistWallet />} />
      </Route>
      <Route path="/scout" element={
        <ProtectedRoute allowedRoles={['scout']}>
          <ScoutLayout />
        </ProtectedRoute>
      }>
        <Route index element={<ScoutDashboard />} />
        <Route path="queue" element={<ScoutQueue />} />
        <Route path="queue/:submissionId" element={<ScoutQueue />} />
        <Route path="hit-list" element={<ScoutHitList />} />
        <Route path="messages" element={<ScoutMessages />} />
        <Route path="shared-songs" element={<ScoutSharedSongs />} />
        <Route path="profile" element={<ScoutProfile />} />
        <Route path="support" element={<SupportPage role="scout" />} />
      </Route>
      <Route path="/admin" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <AdminLayout />
        </ProtectedRoute>
      }>
        <Route index element={<AdminDashboard />} />
        <Route path="users" element={<AdminUsers />} />
        <Route path="submissions" element={<AdminSubmissions />} />
        <Route path="radio-stations" element={<AdminRadioStations />} />
        <Route path="playlist-curators" element={<AdminPlaylistCurators />} />
        <Route path="payment-gateway" element={<AdminPaymentGateway />} />
        <Route path="stripe-payments" element={<AdminStripePayments />} />
        <Route path="ghl-dashboard" element={<AdminGHLDashboard />} />
        <Route path="activity" element={<AdminActivity />} />
        <Route path="backups" element={<AdminBackups />} />
        <Route path="accounting" element={<AdminAccounting />} />
        <Route path="accounting/drift" element={<AdminAccountingDrift />} />
        <Route path="analytics" element={<AdminAnalytics />} />
        <Route path="exports" element={<AdminExports />} />
        <Route path="settings" element={<AdminSettings />} />
        <Route path="messages" element={<AdminMessages />} />
        <Route path="sub-admins" element={<AdminSubAdmins />} />
        <Route path="email-portfolio" element={<AdminEmailPortfolio />} />
        <Route path="credit-packs" element={<AdminCreditPacks />} />
        <Route path="payouts" element={<AdminPayouts />} />
        <Route path="reconciliation" element={<AdminReconciliation />} />
        <Route path="plans" element={<AdminPlans />} />
        <Route path="cms" element={<AdminCMS />} />
        <Route path="moderation" element={<AdminModeration />} />
        <Route path="scout" element={<AdminScoutQueue />} />
        <Route path="audit-log" element={<AdminAuditLog />} />
        <Route path="twilio-health" element={<AdminTwilioHealth />} />
        <Route path="stripe-health" element={<AdminStripeHealth />} />
        <Route path="outreach-campaigns" element={<AdminOutreachCampaigns />} />
        <Route path="archive" element={<AdminArchive />} />
        <Route path="ai-support" element={<AdminAISupport />} />
        <Route path="audit/unclaimed-accepting" element={<AdminUnclaimedAccepting />} />
        <Route path="imports" element={<AdminImports />} />
        <Route path="billboard" element={<AdminBillboard />} />
        <Route path="ad-analytics" element={<AdminAdAnalytics />} />
        <Route path="executive-import" element={<AdminExecutiveImport />} />
        <Route path="evaluations" element={<AdminEvaluations />} />
        <Route path="curator-pricing" element={<AdminCuratorPricing />} />
        <Route path="suggestions" element={<AdminSuggestions />} />
        <Route path="pending-approvals" element={<AdminPendingApprovals />} />
        <Route path="test-cleanup" element={<AdminTestCleanup />} />
        <Route path="accounting-reset" element={<AdminAccountingReset />} />
        <Route path="song-screening" element={<AdminSongScreening />} />
        <Route path="profile-nudge" element={<AdminProfileNudge />} />
        <Route path="alerts" element={<AdminAlerts />} />
        <Route path="executive-sessions" element={<AdminExecutiveSessions />} />
        <Route path="booking-agent" element={<AdminBookingAgent />} />
        <Route path="support" element={<AdminSupportPage />} />
        <Route path="artist-lookup" element={<AdminArtistLookup />} />
        <Route path="knowledge-base" element={<AdminKnowledgeBase />} />
        <Route path="tours" element={<AdminTours />} />
        <Route path="voice-tours" element={<AdminVoiceTours />} />
        <Route path="themes" element={<AdminThemes />} />
        <Route path="landing-stats" element={<AdminLandingStats />} />
        <Route path="support/:ticketId" element={<AdminSupportPage />} />
        <Route path="loyalty" element={<AdminLoyaltyUsers />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </Suspense>
  );
}

function App() {
  return (
    <ThemeProvider>
      <PlatformThemeProvider>
        <AuthProvider>
          <ExecutivePrivilegesProvider>
            <AudioPlayerProvider>
              <BrowserRouter>
                {/* AVE2026 brand strip — the thin yellow→orange→red gradient
                    band that sits above every page on avenuear.com. Sticky
                    so it remains visible at scroll. */}
                <div className="ave-strip" data-testid="ave2026-brand-strip" />
                {/* Sticky red banner rendered on every page while an admin
                    is impersonating another user (via "Sign in as" in the
                    admin kebab). Guarded by a localStorage flag so the
                    component renders nothing when no impersonation is
                    active — zero cost on normal sessions. */}
                <ImpersonationBanner />
                {/* Sticky carbon banner while an admin is in Role Preview
                    mode — shows the previewed role + Back to Admin, and
                    hosts the write-confirmation dialog. */}
                <RolePreviewBanner />
                {/* Non-critical global widgets deferred to `requestIdleCallback`
                    AND lazy-imported so their JS isn't in the main chunk.
                    Suspense fallback is null since none of these render visible
                    UI until user interaction. */}
                <DeferredMount>
                  <Suspense fallback={null}>
                    <GHLChatWidget />
                  </Suspense>
                </DeferredMount>
                <AppRoutes />
                {/* Inline copy editor: applies saved text overrides for ALL
                    visitors and exposes the in-place edit UI to admins only. */}
                <Suspense fallback={null}>
                  <InlineTextEditor />
                </Suspense>
                {/* Applies admin narration overrides + the Voice Tours master
                    switch flag (window.__VOICE_TOURS_ENABLED__) at boot. */}
                <VoiceTourOverridesLoader />
                <DeferredMount>
                  <Suspense fallback={null}>
                    <FloatingHelper />
                  </Suspense>
                </DeferredMount>
                {/* AmbientGuide hidden globally per product call — blue "Next:" hint cards
                    were noisy on every page. Voice narration via FloatingHelper is the
                    single source of guidance now. Re-enable by uncommenting. */}
                {/* <AmbientGuide /> */}
                <Suspense fallback={null}>
                  <Toaster position="top-right" richColors />
                </Suspense>
              </BrowserRouter>
            </AudioPlayerProvider>
          </ExecutivePrivilegesProvider>
        </AuthProvider>
      </PlatformThemeProvider>
    </ThemeProvider>
  );
}

export default App;
